import React, { useEffect, useState } from "react";
import { api, ROLES } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Power, Mail, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const LOBS = [{value:"B2B", label:"B2B"}, {value:"B2C", label:"B2C"}, {value:"BOTH", label:"Both"}];
const DEPTS = ["Admissions","Filing","Enrollment","Commissions","Sales","Operations"];

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [missingLob, setMissingLob] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({});
  const load = () => {
    api.get("/users").then(r=>setRows(r.data));
    if (user?.role === "admin" || user?.role === "management") {
      api.get("/users/missing-lob").then(r=>setMissingLob(r.data)).catch(()=>{});
    }
  };
  useEffect(() => { load(); }, []);
  const canEdit = user?.role === "admin";

  const openNew = () => { setEdit(null); setF({ role: "admissions_officer", line_of_business: "", department: "Admissions", send_invite: true }); setOpen(true); };
  const openEdit = (u) => { setEdit(u); setF({ name: u.name, email: u.email, role: u.role, line_of_business: u.line_of_business||"", department: u.department||"Admissions", is_active: u.is_active !== false }); setOpen(true); };
  const save = async () => {
    if (!f.email) return toast.error("Email required");
    if (!f.line_of_business) return toast.error("Line of Business is required — choose B2B or B2C");
    if (f.line_of_business === "BOTH" && f.role !== "admin") return toast.error("Only Admin users may be set to BOTH");
    try {
      if (edit) { await api.patch(`/users/${edit.user_id}`, f); toast.success("Updated"); }
      else {
        const r = await api.post("/users", f);
        if (f.send_invite === false) toast.success("Employee added (no email sent)");
        else if (r.data?.invite_email_sent) toast.success(`Employee added · Welcome email sent to ${r.data.email}`);
        else toast.success("Employee added · welcome email could not be sent — use Resend Invite to retry");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (u) => { await api.patch(`/users/${u.user_id}`, { is_active: u.is_active === false }); load(); };
  const resendInvite = async (u) => {
    try { await api.post(`/users/${u.user_id}/resend-invite`); toast.success(`Welcome email re-sent to ${u.email}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
  };

  // Delete flow with usage-check
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delUsage, setDelUsage] = useState(null);
  const startDelete = async (u) => {
    setDelTarget(u); setDelOpen(true); setDelUsage(null);
    try { const r = await api.get(`/users/${u.user_id}/usage`); setDelUsage(r.data); }
    catch { setDelUsage({ total_dependents: 0 }); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/users/${delTarget.user_id}`); toast.success("Employee deleted"); setDelOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="heading-font text-3xl font-bold text-slate-900">Users</h1><p className="text-sm text-slate-500 mt-1">Employees who can access the system. Login is restricted to this allowlist.</p></div>
        {canEdit && <Button data-testid="add-user-btn" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2"/>Add Employee</Button>}
      </div>
      {missingLob.length > 0 && (
        <div data-testid="missing-lob-banner" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4"/>Line of Business missing for {missingLob.length} user(s)</div>
          <div className="text-xs mt-1">These users currently have no access to B2B or B2C data. Set their LOB to restore access:</div>
          <ul className="text-xs mt-2 list-disc pl-5 space-y-0.5">
            {missingLob.map(u => <li key={u.user_id}><span className="font-mono">{u.email}</span> — {u.role?.replace(/_/g,' ')}</li>)}
          </ul>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">LoB</th><th className="text-left px-4 py-3">Department</th><th className="text-left px-4 py-3">Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.user_id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3 capitalize">{u.role?.replace(/_/g," ")}</td>
                <td className="px-4 py-3">{u.line_of_business || "—"}</td>
                <td className="px-4 py-3">{u.department || "—"}</td>
                <td className="px-4 py-3">{u.is_active === false ? <span className="stage-badge bg-slate-100 text-slate-700 border-slate-300">Inactive</span> : <span className="stage-badge bg-emerald-50 text-emerald-800 border-emerald-300">Active</span>}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  {canEdit && u.email !== user.email && (<>
                    <Button size="sm" variant="ghost" onClick={()=>resendInvite(u)} data-testid={`resend-invite-${u.email}`} title="Resend welcome email"><Mail className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>openEdit(u)} data-testid={`edit-user-${u.email}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={()=>toggle(u)} title={u.is_active===false?"Activate":"Deactivate"}><Power className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>startDelete(u)} data-testid={`delete-user-${u.email}`} title="Delete" className="text-rose-600 hover:text-rose-800"><Trash2 className="w-4 h-4" /></Button>
                  </>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>{edit ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="user-name" value={f.name||""} onChange={e=>setF({...f, name:e.target.value})}/></div>
            <div><Label>Email *</Label><Input data-testid="user-email" type="email" value={f.email||""} onChange={e=>setF({...f, email:e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role *</Label>
                <Select value={f.role} onValueChange={v=>setF({...f, role:v})}>
                  <SelectTrigger data-testid="user-role"><SelectValue/></SelectTrigger>
                  <SelectContent>{ROLES.map(r=><SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="filing_officer">Filing Officer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Line of Business *</Label>
                <Select value={f.line_of_business} onValueChange={v=>setF({...f, line_of_business:v})}>
                  <SelectTrigger data-testid="user-lob"><SelectValue placeholder="Choose B2B or B2C"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2B">B2B</SelectItem>
                    <SelectItem value="B2C">B2C</SelectItem>
                    {f.role === "admin" && <SelectItem value="BOTH">Both (Admin only)</SelectItem>}
                  </SelectContent>
                </Select>
                {f.line_of_business === "BOTH" && f.role !== "admin" && (
                  <div className="text-[11px] text-rose-600 mt-1">Only Admin users may be set to BOTH.</div>
                )}
              </div>
              <div><Label>Department</Label>
                <Select value={f.department} onValueChange={v=>setF({...f, department:v})}>
                  <SelectTrigger data-testid="user-department"><SelectValue/></SelectTrigger>
                  <SelectContent>{DEPTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {edit && <div><Label>Status</Label>
                <Select value={String(f.is_active !== false)} onValueChange={v=>setF({...f, is_active: v==="true"})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="true">Active</SelectItem><SelectItem value="false">Inactive</SelectItem></SelectContent>
                </Select>
              </div>}
            </div>
            {!edit && (
              <label className="flex items-start gap-2 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <input data-testid="user-send-invite" type="checkbox" className="mt-0.5" checked={f.send_invite !== false} onChange={e=>setF({...f, send_invite: e.target.checked})} />
                <span>
                  <strong>Send welcome email</strong> with the portal link and step-by-step Google sign-in instructions to {f.email ? <span className="font-mono">{f.email}</span> : "the new employee"}.
                </span>
              </label>
            )}
            <p className="text-xs text-slate-500">Employee must sign in with this Google email. Unknown emails are denied.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button data-testid="user-save" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white">{edit ? "Save" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5"/>Delete employee</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div>You are about to remove <strong>{delTarget?.name}</strong> <span className="text-slate-500 font-mono">{delTarget?.email}</span>.</div>
            {!delUsage ? <div className="text-xs text-slate-500">Checking assignments…</div> : delUsage.total_dependents===0 ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900">Nothing assigned. Safe to delete. Consider <em>Deactivate</em> instead if you want to keep audit history under this email.</div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 space-y-1">
                <div className="font-semibold">Can't delete yet — this user is still linked to:</div>
                {delUsage.b2c_applications>0 && <div>· {delUsage.b2c_applications} B2C application(s)</div>}
                {delUsage.b2b_applications>0 && <div>· {delUsage.b2b_applications} B2B application(s)</div>}
                {delUsage.immigration_applications>0 && <div>· {delUsage.immigration_applications} Immigration application(s)</div>}
                {delUsage.open_leads>0 && <div>· {delUsage.open_leads} lead(s)</div>}
                {delUsage.open_tasks>0 && <div>· {delUsage.open_tasks} open task(s)</div>}
                <div className="pt-1">Reassign them to another user first, then retry — or just <strong>Deactivate</strong> to block sign-in without touching assignments.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDelOpen(false)}>Close</Button>
            <Button onClick={confirmDelete} disabled={!delUsage || delUsage.total_dependents>0} data-testid="user-delete-confirm" className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
